import React, { useEffect, useState } from 'react';
import { MdMenuBook, MdOpenInNew } from 'react-icons/md';
import { useSetPageHeader } from '../contexts/PageHeaderContext';
import { getActiveUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';

const ClientHandBook: React.FC = () => {
    usePageMeta({
        title: 'Client Handbook | HCAE Client Portal',
        description: 'View the HCAE client handbook.',
    });
    useSetPageHeader('Client Handbook', 'Your guide to HCAE services and policies');

    const user = getActiveUserInfo();
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);

    useEffect(() => {
        const fetchHandbookUrl = async () => {
            try {
                setLoading(true);
                setError(false);
                const response = await fetch(`${BASE_URL}setting/list?key=client_handbook_url`, {
                    headers: {
                        'Content-Type': 'application/json',
                        token: `${user?.api_token}`,
                    },
                });
                const data = await response.json();
                const url = data?.data?.client_handbook_url || data?.data?.value || data?.data || null;
                if (url && typeof url === 'string') {
                    setPdfUrl(url);
                } else {
                    setError(true);
                }
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchHandbookUrl();
    }, []);

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-pulse">
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
                        <div className="w-8 h-8 rounded-xl bg-brand-100" />
                        <div className="h-4 w-48 rounded-full bg-brand-100" />
                    </div>
                    <div className="w-full bg-slate-50" style={{ height: 'min(75vh, calc(100vh - 280px))' }} />
                </div>
            </div>
        );
    }

    if (error || !pdfUrl) {
        return (
            <div className="max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center justify-center py-24">
                    <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
                        <MdMenuBook size={30} />
                    </div>
                    <p className="text-slate-400 font-medium">Client handbook is not available at the moment.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
                    <div className="flex items-center gap-2">
                        <MdMenuBook size={18} className="text-brand-600" />
                        <h3 className="font-semibold text-slate-800 text-sm">Client Handbook</h3>
                    </div>
                    <button
                        onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                    >
                        <MdOpenInNew size={14} />
                        Open
                    </button>
                </div>

                <div className="relative bg-slate-50">
                    {!iframeLoaded && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <div className="w-full h-full p-4 sm:p-6">
                                <div className="h-full w-full rounded-xl bg-white border border-slate-100 shadow-sm animate-pulse" />
                            </div>
                        </div>
                    )}
                    <iframe
                        src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="w-full"
                        style={{ height: 'min(80vh, calc(100vh))' }}
                        title="Client Handbook"
                        onLoad={() => setIframeLoaded(true)}
                    />
                </div>
            </div>
        </div>
    );
};

export default ClientHandBook;
